/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { accessAPI } from "../../utils";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { FileUpload } from "primereact/fileupload";
import { Calendar } from "primereact/calendar";
import { addLocale } from "primereact/api";
import { Dropdown } from "primereact/dropdown";
import { Password } from "primereact/password";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { Tooltip } from "primereact/tooltip";

import { Toast } from "primereact/toast";

// El Calendar de PrimeReact necesita un Date como value; con un string ISO
// (como devuelve la API) muestra el campo vacío. Se compensa el offset para
// que una fecha UTC (00:00Z) no retroceda un día en zonas horarias negativas
// (mismo criterio que formatFecha de utils).
function normalizarFechaRespuesta(valor) {
  if (!valor) return null;
  if (valor instanceof Date) return valor;
  const fecha = new Date(valor);
  if (isNaN(fecha.getTime())) return null;
  return new Date(fecha.getTime() + fecha.getTimezoneOffset() * 60 * 1000);
}

export default function FormularioCampo(props) {
  const campoRef = useRef();
  const { campo, contenidoCampos } = props;
  const [opciones, setOpciones] = useState([]);
  const [queryparamsActual, setQueryparamsActual] = useState([]);
  const [loaderTextoProvisorio, setLoaderTextoProvisorio] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);

  const [contenidoActual, setContenidoActual] = useState(
    campo.tipoitemformularioid === 7
      ? normalizarFechaRespuesta(campo.respuesta)
      : campo.respuesta
  );
  const [seleccionesMultiples, setSeleccionesMultiples] = useState(
    Array.isArray(campo.respuesta) ? campo.respuesta : []
  );

  // Estado del campo compuesto de precio (tipo 14): valor { precio, monedaId }.
  const [precioValor, setPrecioValor] = useState(campo.respuesta?.precio ?? null);
  const [monedaPrecioValor, setMonedaPrecioValor] = useState(
    campo.respuesta?.monedaId ?? null
  );
  const [monedasPrecio, setMonedasPrecio] = useState([]);

  // listo se utiliza para cuando no tengo datos para mostrar pero no quiero mostrar el loader
  const [listo, setListo] = useState(false);
  const [loader, setLoader] = useState(true);

  // Estado para las opciones
  const [opcionesDeMapeo, setopcionesDeMapeo] = useState([]);
  const [inputopcionesDeMapeo, setInputopcionesDeMapeo] = useState("");

  const toast = useRef(null);

  // Añade el locale en español
  addLocale("es", {
    firstDayOfWeek: 1,
    showMonthAfterYear: true,
    dayNames: [
      "domingo",
      "lunes",
      "martes",
      "miércoles",
      "jueves",
      "viernes",
      "sábado"
    ],
    dayNamesShort: ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"],
    dayNamesMin: ["D", "L", "M", "X", "J", "V", "S"],
    monthNames: [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre"
    ],
    monthNamesShort: [
      "ene",
      "feb",
      "mar",
      "abr",
      "may",
      "jun",
      "jul",
      "ago",
      "sep",
      "oct",
      "nov",
      "dic"
    ],
    today: "Hoy",
    clear: "Limpiar"
  });

  //Genera el string a mandar a la base de datos que luego se guarda como un array y se separa mediate .split(" ; ")
  const generateString = () => {
    const stringResult = opcionesDeMapeo.join(" ; ");

    return stringResult;
  };

  // Cuando cambia el campo, actualiza el padre
  function campoOnChange() {
    // Procesa el contenido y llama a la función del formulario

    let contenido;
    if (campo.tipoitemformularioid === 6) {
      contenido = campoRef.current.checked;
    } else if (campo.tipoitemformularioid === 9) {
      if (inputopcionesDeMapeo) {
        setopcionesDeMapeo([...opcionesDeMapeo, inputopcionesDeMapeo]);
        setInputopcionesDeMapeo("");
      }
      contenido = generateString();
    } else {
      contenido = campoRef.current.value;
    }

    props.onUpdate(props.campo.nombre, contenido);

    // Si el campo tiene un onChange, se ejecuta pasándole el contenido como parámetro
    if (props.campo.onChange) {
      props.campo.onChange(contenido);
    }

    // Si el campo tiene el parámetro onChangeRequestURL, hace el llamado
    if (props.campo.onChangeRequestURL) {
      setLoaderTextoProvisorio(true);
      // Si ya existe un timeout previo, lo cancelamos
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }

      // Creamos un nuevo timeout de 500ms para ejecutar la búsqueda
      setTypingTimeout(
        setTimeout(() => {
          accessAPI(
            "POST",
            props.campo.onChangeRequestURL,
            { contenido },
            () => {
              setLoaderTextoProvisorio(false);
            },
            (respuesta) => {
              setLoaderTextoProvisorio(false);
              toast.current.show({
                severity: "error",
                summary: "Error",
                detail:
                  respuesta[0].msg ||
                  "Ocurrió un error, intente nuevamente más tarde",
                sticky: true
              });
            }
          );
        }, 1000)
      );
    }
  }

  // Update llamado si el input es un filepicker
  function archivoUpdate(evento) {
    props.actualizarArchivo(props.campo.nombre, evento.files[0]);
  }

  // Update llamado si el input es date
  function fechaUpdate(value) {
    props.onUpdate(props.campo.nombre, value);
    setContenidoActual(value);
  }

  // Update llamado si el input es dropdown
  function dropdownUpdate(value) {
    // PrimeReact Dropdown emite `undefined` al limpiar (showClear). Normalizamos
    // a null para que se serialice en JSON.stringify y llegue al API.
    const normalizado = value === undefined ? null : value;
    props.onUpdate(props.campo.nombre, normalizado);
    setContenidoActual(normalizado);
    // Si el campo tiene un onChange, se ejecuta pasándole el valor como parámetro
    if (props.campo.onChange) {
      props.campo.onChange(normalizado);
    }
  }

  // Update llamado si el input es un número
  function numeroUpdate(value) {
    props.onUpdate(props.campo.nombre, value.value);
    setContenidoActual(value.value);
  }

  // Update llamado si el input es checkbox
  function checkboxUpdate(e) {
    props.onUpdate(props.campo.nombre, e.checked);
    setContenidoActual(e.checked);
  }

  // Update llamado si el input es multi-select dropdown
  function multiSelectUpdate(value) {
    if (seleccionesMultiples.includes(value)) return;
    const nuevas = [...seleccionesMultiples, value];
    setSeleccionesMultiples(nuevas);
    props.onUpdate(campo.nombre, nuevas);
    setContenidoActual(null);
  }

  function multiSelectRemove(value) {
    const nuevas = seleccionesMultiples.filter((v) => v !== value);
    setSeleccionesMultiples(nuevas);
    props.onUpdate(campo.nombre, nuevas);
  }

  // Update llamado si el input es multi-text list (tipo 13)
  function multiTextAdd(texto) {
    const trimmed = texto.trim();
    if (!trimmed || seleccionesMultiples.includes(trimmed)) return;
    const nuevas = [...seleccionesMultiples, trimmed];
    setSeleccionesMultiples(nuevas);
    props.onUpdate(campo.nombre, nuevas);
  }

  // Los tipos de campo son:
  // 1 - texto
  // 2 - email
  // 3 - password
  // 4 - numero entero
  // 5 - dropdown
  // 6 - checkbox
  // 7 - Date
  // 8 - numero float
  // 9 - Option map = string separaada por espacio ; espacio
  // Cuando carga el componente verifica si el tipo es dropdown y si le pasaron el array de opciones
  // 10 - Archivo
  // 11 - textarea
  // 12 - Multi-select dropdown
  // 13 - Multi-text list
  // 14 - Precio: selector de moneda (muestra sólo el símbolo) + número float.
  //      Emite el valor compuesto { precio, monedaId }. Las monedas se cargan
  //      automáticamente desde el sistema (no se pasan por config).

  useEffect(() => {
    if (campo) {
      if (
        campo.tipoitemformularioid === 5 ||
        campo.tipoitemformularioid === 12
      ) {
        // Si no tiene queryparams y ya no cargó las opciones
        if (!campo.queryparams) {
          if (opciones.length === 0) {
            // Si le pasa las opciones como array, las asigna
            if (Array.isArray(campo.opciones)) {
              setListo(true);
              setOpciones(campo.opciones);
            } else {
              // Si no se lo pasaron, va a buscar las opciones
              accessAPI(
                "GET",
                campo.opciones,
                null,
                (respuesta) => {
                  setOpciones(respuesta);
                  setListo(true);
                },
                (respuesta) => {
                  setListo(true);
                  toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail:
                      respuesta[0].msg ||
                      "Ocurrió un error, intente nuevamente más tarde",
                    sticky: true
                  });
                }
              );
            }
          }
        } else {
          // Si tiene queryparams, tiene que determinarlos antes de ir a buscar las opciones
          setLoader(false);
          let url = `${campo.opciones}?`;
          let camposCompletos = true;
          let cambioQueryparams = false;
          // Verifica que esté seteado y que haya cambiado desde la última vez
          for (const queryparam of campo.queryparams) {
            if (!contenidoCampos[queryparam]) {
              camposCompletos = false;
            } else {
              url = url + `${queryparam}=${contenidoCampos[queryparam]}&`;
              // Verifica que haya cambiado desde la última vez
              if (
                contenidoCampos[queryparam] !== queryparamsActual[queryparam]
              ) {
                cambioQueryparams = true;
              }
            }
          }
          // Si están todos los queryparams, y cambiaron desde la última vez
          if (camposCompletos && cambioQueryparams) {
            setQueryparamsActual(contenidoCampos);
            setLoader(true);
            accessAPI(
              "GET",
              url,
              null,
              (respuesta) => {
                setOpciones(respuesta);
                setListo(true);
              },
              (respuesta) => {
                setListo(true);
                toast.current.show({
                  severity: "error",
                  summary: "Error",
                  detail:
                    respuesta[0].msg ||
                    "Ocurrió un error, intente nuevamente más tarde",
                  sticky: true
                });
              }
            );
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campo, contenidoCampos]);

  // Carga de monedas para el campo de precio (tipo 14). Se traen siempre desde
  // el sistema (no se pasan por config); se muestra sólo el símbolo.
  useEffect(() => {
    if (campo.tipoitemformularioid !== 14) return;
    accessAPI(
      "GET",
      "moneda",
      null,
      (respuesta) => {
        setMonedasPrecio(
          (respuesta || []).map((m) => ({
            value: m.id,
            simbolo: m.simbolo,
            etiqueta: m.simbolo ? `${m.nombre} (${m.simbolo})` : m.nombre
          }))
        );
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar las monedas",
          sticky: true
        });
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campo.tipoitemformularioid]);

  // Cuando carga las opciones apaga el loader y establece el valor inicial
  useEffect(() => {
    if (opciones.length > 0) {
      setLoader(false);
      // Si hay una respuesta inicial, actualiza el contenido actual
      if (campo.respuesta !== undefined && campo.respuesta !== null) {
        // Verifica que el valor exista en las opciones
        const valorExiste = opciones.some(
          (opcion) => opcion.value === campo.respuesta
        );
        if (valorExiste) {
          setContenidoActual(campo.respuesta);
          // Actualiza también en el padre
          props.onUpdate(campo.nombre, campo.respuesta);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opciones]);

  // Emite el valor compuesto { precio, monedaId } del campo de precio (tipo 14).
  // Preserva 0 como valor válido y null como "sin precio".
  function precioCompuestoUpdate(nuevoPrecio, nuevaMoneda) {
    setPrecioValor(nuevoPrecio);
    setMonedaPrecioValor(nuevaMoneda);
    props.onUpdate(campo.nombre, {
      precio: nuevoPrecio ?? null,
      monedaId: nuevaMoneda ?? null
    });
  }

  // Muestra sólo el símbolo de la moneda en el dropdown de precio.
  const simboloMoneda = (opcion) =>
    opcion ? opcion.simbolo || opcion.etiqueta || "" : "";

  return (
    <div css={{ display: "flex", flexDirection: "column" }}>
      <Toast ref={toast} position="top-center" />
      <Tooltip target=".pi-question-circle" />
      {campo.tipoitemformularioid !== 6 && (
        <div css={{ marginBottom: "8px", fontSize: "0.9em" }}>
          <div css={{ display: "flex", flexDirection: "row", gap: "6px" }}>
            <span css={{ alignSelf: "center" }}>{campo.etiqueta}</span>
            {campo.notas && (
              <span css={{ alignSelf: "center" }}>
                <i
                  className="pi pi-question-circle"
                  data-pr-tooltip={campo.notas}
                  data-pr-position="top"
                ></i>
              </span>
            )}
          </div>
        </div>
      )}
      {(campo.tipoitemformularioid === 1 ||
        campo.tipoitemformularioid === 2) && (
        <InputText
          id="campo"
          defaultValue={props.campo.respuesta ? props.campo.respuesta : null}
          onChange={campoOnChange}
          ref={campoRef}
          disabled={props.disabled}
          invalid={props.estado === "error"}
        />
      )}
      {campo.tipoitemformularioid === 3 && (
        <Password
          onChange={(e) => {
            props.onUpdate(props.campo.nombre, e.target.value);
            setContenidoActual(e.target.value);
            if (props.campo.onChange) {
              props.campo.onChange(e.target.value);
            }
          }}
          disabled={props.disabled}
          value={contenidoActual || ""}
          invalid={props.estado === "error"}
          toggleMask
          feedback={false}
        />
      )}
      {campo.tipoitemformularioid === 4 && (
        <InputNumber
          value={props.campo.respuesta ? props.campo.respuesta : null}
          onChange={numeroUpdate}
          disabled={props.disabled}
          invalid={props.estado === "error"}
          ref={campoRef}
          locale="es-UY"
        />
      )}
      {campo.tipoitemformularioid === 5 && (
        <Dropdown
          loading={loader && !listo}
          placeholder={
            loader && !listo
              ? "Cargando opciones..."
              : !loader && !listo
                ? "Esperando otro campo..."
                : opciones && opciones.length === 0
                  ? "No hay opciones disponibles"
                  : ""
          }
          options={opciones}
          disabled={props.disabled || (!loader && !listo) || (opciones && opciones.length === 0)}
          invalid={props.estado === "error"}
          optionLabel="etiqueta"
          optionValue="value"
          optionDisabled="disabled"
          checkmark
          filter
          filterBy="etiqueta"
          emptyFilterMessage="Sin resultados"
          showClear={campo.opcionVacia === true}
          onChange={(e) => dropdownUpdate(e.value)}
          value={contenidoActual}
          css={{ width: "100%" }}
        />
      )}
      {campo.tipoitemformularioid === 6 && (
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "8px"
          }}
        >
          <Checkbox
            onChange={checkboxUpdate}
            disabled={props.disabled}
            checked={contenidoActual ?? props.campo.respuesta ?? false}
            invalid={props.estado === "error"}
          />
          <label
            css={{
              fontSize: "0.9em",
              cursor: props.disabled ? "default" : "pointer"
            }}
            onClick={() => {
              if (!props.disabled) {
                checkboxUpdate({ checked: !contenidoActual });
              }
            }}
          >
            <span>{campo.etiqueta}</span>
            {campo.notas && (
              <span css={{ marginLeft: "6px" }}>
                <i
                  className="pi pi-question-circle"
                  data-pr-tooltip={campo.notas}
                  data-pr-position="top"
                ></i>
              </span>
            )}
          </label>
        </div>
      )}
      {campo.tipoitemformularioid === 7 && (
        <>
          <Calendar
            value={contenidoActual}
            onChange={(e) => fechaUpdate(e.value)}
            locale="es"
            dateFormat="dd/mm/yy"
            showIcon
            disabled={props.disabled}
            invalid={props.estado === "error"}
            ref={campoRef}
          />
        </>
      )}
      {campo.tipoitemformularioid === 8 && (
        <InputNumber
          value={props.campo.respuesta ? props.campo.respuesta : null}
          onChange={numeroUpdate}
          disabled={props.disabled}
          invalid={props.estado === "error"}
          ref={campoRef}
          locale="es-UY"
          useGrouping={false}
          maxFractionDigits={20}
        />
      )}
      {campo.tipoitemformularioid === 9 && (
        <>
          <input
            type="text"
            value={inputopcionesDeMapeo}
            onChange={(e) => setInputopcionesDeMapeo(e.target.value)}
          />
          <button type="button" onClick={campoOnChange}>
            +
          </button>
          <p>Valores agregados: {opcionesDeMapeo.join(", ")}</p>
        </>
      )}
      {campo.tipoitemformularioid === 10 && (
        <FileUpload
          mode="basic"
          name="demo[]"
          onSelect={(e) => archivoUpdate(e)}
          disabled={props.disabled}
          ref={campoRef}
          customUpload
          cancelLabel="Cancelar"
          chooseLabel="Seleccionar"
          style={{
            border:
              props.estado === "error"
                ? "1px solid red"
                : "1px solid rgba(0,0,0,0)",
            borderRadius: "6px"
          }}
        />
      )}
      {campo.tipoitemformularioid === 11 && (
        <InputTextarea
          defaultValue={props.campo.respuesta ? props.campo.respuesta : null}
          disabled={props.disabled}
          onChange={campoOnChange}
          rows={10}
          cols={30}
          ref={campoRef}
          invalid={props.estado === "error"}
        />
      )}
      {campo.tipoitemformularioid === 12 && (
        <div css={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Dropdown
            loading={loader && !listo}
            placeholder={
              loader && !listo
                ? "Cargando opciones..."
                : !loader && !listo
                  ? "Esperando otro campo..."
                  : ""
            }
            options={opciones?.filter(
              (o) => !seleccionesMultiples.includes(o.value)
            )}
            disabled={props.disabled || (!loader && !listo)}
            invalid={props.estado === "error"}
            optionLabel="etiqueta"
            optionValue="value"
            filter
            filterBy="etiqueta"
            emptyFilterMessage="Sin resultados"
            onChange={(e) => multiSelectUpdate(e.value)}
            value={null}
            css={{ width: "100%" }}
          />
          {seleccionesMultiples.length > 0 && (
            <div
              css={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px"
              }}
            >
              {seleccionesMultiples.map((value) => {
                const opcion = opciones?.find((o) => o.value === value);
                return (
                  <span
                    key={value}
                    className="p-chip p-component"
                    css={{ display: "inline-flex", alignItems: "center" }}
                  >
                    <span className="p-chip-text">{opcion ? opcion.etiqueta : value}</span>
                    {!props.disabled && (
                      <span
                        className="p-chip-remove-icon pi pi-times-circle"
                        css={{ cursor: "pointer", marginLeft: "0.5rem" }}
                        onClick={() => multiSelectRemove(value)}
                      />
                    )}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}
      {campo.tipoitemformularioid === 13 && (
        <div css={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <InputText
            placeholder="Escribir y presionar Enter para agregar..."
            disabled={props.disabled}
            invalid={props.estado === "error"}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                multiTextAdd(e.target.value);
                e.target.value = "";
              }
            }}
          />
          {seleccionesMultiples.length > 0 && (
            <div
              css={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
              }}
            >
              {seleccionesMultiples.map((texto, index) => (
                <span
                  key={texto + "-" + index}
                  className="p-chip p-component"
                  css={{ display: "inline-flex", alignItems: "center" }}
                >
                  <span className="p-chip-text">{texto}</span>
                  {!props.disabled && (
                    <span
                      className="p-chip-remove-icon pi pi-times-circle"
                      css={{ cursor: "pointer", marginLeft: "0.5rem" }}
                      onClick={() => multiSelectRemove(texto)}
                    />
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      {campo.tipoitemformularioid === 14 && (
        <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Dropdown
            value={monedaPrecioValor}
            options={monedasPrecio}
            onChange={(e) => precioCompuestoUpdate(precioValor, e.value)}
            optionLabel="etiqueta"
            optionValue="value"
            valueTemplate={(opcion) => (
              <span>{opcion ? simboloMoneda(opcion) : "—"}</span>
            )}
            itemTemplate={(opcion) => <span>{simboloMoneda(opcion)}</span>}
            placeholder="—"
            disabled={props.disabled}
            invalid={props.estado === "error"}
            style={{ flexShrink: 0 }}
          />
          <InputNumber
            value={precioValor}
            onValueChange={(e) =>
              precioCompuestoUpdate(e.value ?? null, monedaPrecioValor)
            }
            disabled={props.disabled}
            invalid={props.estado === "error"}
            mode="decimal"
            minFractionDigits={0}
            maxFractionDigits={2}
            locale="es-UY"
            style={{ flex: 1 }}
            inputStyle={{ width: "100%" }}
          />
        </div>
      )}
      {props.mensaje && props.estado === "error" && (
        <small
          css={{
            color: "var(--rojo)",
            fontWeight: "600",
            textAlign: "right"
          }}
        >
          {props.mensaje}
        </small>
      )}
      {(!props.mensaje || props.estado !== "error") && (
        <div css={{ height: "1em" }} />
      )}
      {props.campo.onChangeRequestURL && (
        <div
          css={{
            display: "flex",
            justifyContent: "flex-end",
            width: "100%",
            textAlign: "right",
            height: "20px",
            color: "var(--principal)"
          }}
        >
          {loaderTextoProvisorio && (
            <div
              css={{
                alignSelf: "center",
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end"
              }}
            >
              <span>Sincronizando...</span>
              <i className="pi pi-spin pi-spinner"></i>
            </div>
          )}
          {!loaderTextoProvisorio && (
            <div css={{ alignSelf: "center" }}>
              <span>Sincronizado</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
